import { useMemo, useState } from "react";
import type { WidgetConfig } from "@/lib/schema";
import { getWidgetModule } from "@/widgets/registry";
import { partsOf } from "@/widgets/types";
import { Modal } from "./Modal";
import { ScoringField } from "./ScoringField";

interface Props {
  open: boolean;
  mode: "create" | "edit";
  initial: WidgetConfig | null;
  allWidgets: WidgetConfig[];
  onSave: (config: WidgetConfig) => void;
  onCancel: () => void;
}

export function WidgetSettingsModal({
  open,
  mode,
  initial,
  allWidgets,
  onSave,
  onCancel,
}: Props) {
  if (!open || !initial) return null;
  return (
    <ModalBody
      key={initial.id}
      mode={mode}
      initial={initial}
      allWidgets={allWidgets}
      onSave={onSave}
      onCancel={onCancel}
    />
  );
}

function ModalBody({
  mode,
  initial,
  allWidgets,
  onSave,
  onCancel,
}: {
  mode: "create" | "edit";
  initial: WidgetConfig;
  allWidgets: WidgetConfig[];
  onSave: (config: WidgetConfig) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<WidgetConfig>(initial);
  const widget = useMemo(() => getWidgetModule(draft.kind), [draft.kind]);

  if (!widget) {
    return (
      <Modal open onClose={onCancel} data-testid="widget-settings-modal">
        <h2>Ukjent widget-type</h2>
        <p>Kunne ikke laste «{draft.kind}».</p>
        <div className="modal-actions">
          <button
            type="button"
            className="btn ghost"
            onClick={onCancel}
            data-testid="widget-cancel-button"
          >
            Lukk
          </button>
        </div>
      </Modal>
    );
  }

  const validation = widget.validate(draft);
  const errors = validation.ok ? {} : validation.errors;
  const canSave = validation.ok;

  const handleChange = (patch: Partial<WidgetConfig>) => {
    setDraft((d) => ({ ...d, ...patch }) as WidgetConfig);
  };

  const handleSave = () => {
    if (!canSave) return;
    onSave(widget.normalize ? widget.normalize(draft) : draft);
  };

  const Editor = widget.SettingsEditor;
  return (
    <Modal
      open
      onClose={onCancel}
      data-testid="widget-settings-modal"
      size="lg"
    >
      <h2>{mode === "create" ? "Ny widget" : "Rediger widget"}</h2>
      <Editor
        config={draft}
        onChange={handleChange}
        errors={errors}
        allWidgets={allWidgets}
        mode={mode}
        scoringField={
          // A kind that declares scorable parts carries one goal per part and
          // renders them itself, so the shared single-goal field would be a
          // third, contradictory input (docs/adr/0010).
          partsOf(widget, draft).length > 0 ? null : (
            <ScoringField
              module={widget}
              config={draft}
              onChange={handleChange}
              error={errors.weekGoal}
            />
          )
        }
      />
      <div className="modal-actions">
        <button
          type="button"
          className="btn ghost"
          onClick={onCancel}
          data-testid="widget-cancel-button"
        >
          Avbryt
        </button>
        <button
          type="button"
          className="btn"
          disabled={!canSave}
          onClick={handleSave}
          data-testid="widget-save-button"
        >
          Lagre
        </button>
      </div>
    </Modal>
  );
}
